import { Fragment, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as userApi from '../api/User';
import { ICompany, IUserCompanies, IUserProfile, IUserSettings } from '../util/DataModelInterface'
import { Spinner } from '../components/Spinner';
import { ToastWrap } from '../components/ToastWrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SearchModal } from '../components/SearchModal';
import { getCompanies } from '../api/Company';
import { ListGroup } from 'react-bootstrap';
import { ConfirmModal } from '../components/ConfirmModal';
import useToastHandler from '../hooks/useToastHandler';

interface IUserValidation {
  name: boolean;
  email: boolean;
  phone: boolean;
  // language: boolean;
}

export const User = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { addToast } = useToastHandler();

    const [user, setUser] = useState(state.user as IUserProfile);
    const [companies, setCompanies] = useState([] as ICompany[]);
    const [activeCompanies, setActiveCompanies] = useState([] as ICompany[]);
    const [selectedCompanies, setSelectedCompanies] = useState([] as IUserCompanies[]);
    const [saving, setSaving] = useState(false);
    const [expandDetails, setExpandDetails] = useState(false);
    const [expandCompanies, setExpandCompanies] = useState(false);
    const [changed, setChanged] = useState(false);
    const [changedDetails, setChangedDetails] = useState({
      details: false,
      companies: false
    });
    // const [isValidForm, setIsValidForm] = useState(true);
    const [userExistsError, setUserExistsError] = useState(false);
    const [toastActive, setToastActive] = useState(false);
    const [toastHeader, setToastHeader] = useState("");
    const [toastText, setToastText] = useState("");
    const [toastType, setToastType] = useState("");
    const [modalActive, setModalActive] = useState(false);
    const [confirmModalActive, setConfirmModalActive] = useState(false);
    const [removedItem, setRemovedItem] = useState({} as IUserCompanies);
    const [isArchive, setIsArchive] = useState(false);
    const [confirmArchModalActive, setConfirmArchModalActive] = useState(false);
    const [confirmModalTitle, setConfirmModalTitle] = useState("");
    const [confirmModalContent, setConfirmModalContent] = useState("");
    const [userState, setUserState] = useState({
      userId: user?.user_id,
      name: user?.name ? user.name : "",
      email: user?.user_name ? user.user_name : "",
      language: user?.language ? user.language : "FI",
      phone: user?.phone ? user.phone : "",
      reporter: user?.reporter ? user.reporter : false,
      information: user?.information ? user.information : false,
      archived: user?.archived ? user.archived : false
    });
    const [userStateClass, setUserStateClass] = useState({
      name: "",
      email: "",
      language: "",
      phone: "",
      reporter: "",
      information: ""
    })

    const comparer = (a: any, b: any) => {
      return a.name.localeCompare(b.name);
    }

    useEffect(() => {
      getCompanies().then((data: any) => {
        // console.log("user companies data", data);
        if (data?.error) {
          addToast(data.error, 'users', 'fetch');
        } else if (data) {
          setCompanies(data);
          setActiveCompanies(data.filter((c: ICompany) => c.archived === false));
        }
      }).finally(() => {
      });
    }, [addToast]);

    useEffect(() => {
      let userComp = [] as IUserCompanies[];
      if (user && selectedCompanies.length === 0) {
        user.companies.forEach((c) => {
          userComp.push(c);
        })
        setSelectedCompanies(userComp.sort(comparer));
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // console.log("userState", userState);

    const isValidEmail = (email: string) => {
      // const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      // eslint-disable-next-line no-useless-escape
      const regex = new RegExp('^[^@<> ]+@[^@<> ]+\.[^@<> ]+$');
      return regex.test(String(email).toLowerCase())
  }

    const validationRules: IUserValidation = {
      name: !!userState.name && userState.name.match(/^ *$/) === null,
      email: !!userState.email && isValidEmail(userState.email),
      phone: true,
      // language: !!userState.language && userState.language.match(/^ *$/) === null
    }

    const update = (event: { currentTarget: any; }) => {
      const target = event.currentTarget

      setUserState({
          ...userState,
          [target.name]: target.type === 'checkbox'
              ? target.checked
              : target.value
      })
      setUserStateClass({...userStateClass, [target.name]: 'fieldchanged'});
      setChanged(true);
      setChangedDetails({...changedDetails, details: true});
      if (target.name === "email") {
        setUserExistsError(false);
      }
    }

    // Set states to saved
    const setSavedStates = () => {
      setUserStateClass({
        name: 'fieldsaved',
        email: 'fieldsaved',
        language: 'fieldsaved',
        phone: 'fieldsaved',
        reporter: 'fieldsaved',
        information: 'fieldsaved'
      });
      setUser({...user, name: userState.name});
      setToastHeader(intl.formatMessage({id: 'users.saved'}));
      setToastText("");
      setToastType('success');
      setToastActive(true);
      setChangedDetails({
        details: false,
        companies: false
      });
    }

    const saveUser = async (e: { preventDefault: () => void; } | undefined) => {

      e?.preventDefault(); // needed?

      const isValidForm = Object
        .values(validationRules)
        .every(key => key)

      // setIsValidForm(isValidForm);

      if (!isValidForm) {
          console.log('Not valid form');
          setToastHeader(intl.formatMessage({id: 'error.users.add'}));
          setToastText(intl.formatMessage({id: 'users.fail.info'}));
          setToastType('warning');
          setToastActive(true);
          return;
      }

      let user =  {
        user_id: userState.userId,
        user_name: userState.email,
        name: userState.name,
        language: userState.language,
        archived: userState.archived,
        phone: userState.phone,
        companies: [],
        reporter: userState.reporter,
        information: userState.information
      } as IUserSettings;

      setSaving(true);
      setChanged(false);

      if (!userState.userId) { // User does not exist yet
        let resp = await userApi.addUser(user);
        if (resp.error && resp.error.includes('User already exists')) {
          setUserExistsError(true);
          setToastHeader(intl.formatMessage({id: 'error.users.add'}));
          setToastText(intl.formatMessage({id: 'users.fail.info'}));
          setToastType('warning');
          setToastActive(true);
        } else if (resp?.error) {
          addToast(resp.error, 'users', 'add');
        } else {
          let user_id = resp.user_id;
          // console.log("user_id", user_id);
          setUserState({...userState, userId: user_id});
          await userApi.getAllUsers(true);
          setSavedStates();
        }
      }
      else { // Modify user
        // add company ids
        let userCompIds = [] as number[];
        selectedCompanies.forEach(((s) => {
          let comp = companies.find((c: any) => c["id"] === s.id);
          if (comp?.id) userCompIds.push(parseInt(comp.id));
        }));
        user.companies = userCompIds;
        let resp = await userApi.modifyUser(user);
        if (resp?.error) {
          addToast(resp.error, 'users', 'modify');
        }
        await userApi.getAllUsers(true);
        setSavedStates();

      }
      setSaving(false);
    }

    const validate = (field: keyof IUserValidation) => {
      // return ( !isValidForm && !userState[field] ) ||
      //   ( userState[field] && !validationRules[field] ) ||
      //   ( userExistsError && field === "email")
      //     ? 'has-error'
      //     : undefined
      return ((!validationRules[field] ||
        ( userExistsError && field === "email")) ? 'has-error' : '')
    }

    const handleSelectedItems = (sel: IUserCompanies[]) => {
      let newSelectedCompanies = selectedCompanies;
      sel.forEach((s, i) => {
        if (!newSelectedCompanies.find(n => n.id === s.id)) newSelectedCompanies.push(s);
      });
      setSelectedCompanies(newSelectedCompanies.sort(comparer));
      setChanged(true);
      setChangedDetails({...changedDetails, companies: true});
    }

    const removeItem = (item: IUserCompanies) => {
      let newSelectedCompanies = selectedCompanies.filter((s) => s.id !== item.id);
      setSelectedCompanies(newSelectedCompanies);
      setChanged(true);
    }

    const confirmRemoveItem = (item: IUserCompanies) => {
      setConfirmModalActive(true);
      setRemovedItem(item);
      setChangedDetails({...changedDetails, companies: true});
    }

    const getCompanyItem = (item: IUserCompanies, ind: number) => {
      let company = companies.find((c) => c.id === item.id);
      return (
        <Fragment key={ind}>
        { company ?
          <ListGroup.Item className="d-flex flex-row align-items-center">
            <div className="company">
              <span className="title">
                <Link to='/companymanagement/company' state={{ company }} replace={false}>
                  { company.name }
                </Link>
              </span>
            </div>
            <button className="btn btn-link p-0 my-0 ms-auto" onClick={() => confirmRemoveItem(item)}>
              <i className="me-2 fa fa-trash"></i>
              <FormattedMessage id="button.remove"/>
            </button>
          </ListGroup.Item>
          : null }
        </Fragment>
      );
    }

    const archiveUser = async (archive: boolean) => {

      let user =  {
        user_id: userState.userId,
        user_name: userState.email,
        name: userState.name,
        archived: archive
      } as IUserSettings;

      setSaving(true);
      let resp = await userApi.archiveUser(user);
      if (resp?.error) {
        addToast(resp.error, 'users', 'archive');
      }
      setUserState({...userState, archived: archive});
      await userApi.getAllUsers(true);
      setSaving(false);
    }

    const confirmArchiveUser = () => {
      let archived = !userState.archived;
      setIsArchive(archived);

      let title = archived ? intl.formatMessage({id: 'confirm.archive'}) : intl.formatMessage({id: 'confirm.activate'});
      let textContent = archived ? intl.formatMessage({id: 'confirm.archive.content'}) : intl.formatMessage({id: 'confirm.activate.content'});

      setConfirmModalTitle(title);
      setConfirmModalContent(textContent);
      setConfirmArchModalActive(true);
    }

    const confirmContent = () => <div>{intl.formatMessage({id: 'confirm.remove.content'})}</div>;
    const confirmArchiveContent = () => <div>{confirmModalContent}</div>;

    return (
      <>
        <SearchModal
          active={modalActive}
          // searchData={companies.map( (c) => c.name )}
          searchData={activeCompanies}
          modalId='addCompanyModal'
          onClose={() => setModalActive(false)}
          onSelect={(compList: IUserCompanies[]) => handleSelectedItems(compList)}
          title={intl.formatMessage({id: 'users.addcompany'})}
          findText={intl.formatMessage({id: 'users.findcompany'})}
          placeHolder={intl.formatMessage({id: 'users.pickcompany'})}
          foundItemsTitle={intl.formatMessage({id: 'users.companiestoadd'})}
          filterByFields={['name']}
          />
        <ConfirmModal
          active={confirmModalActive}
          modalId='deleteItemModal'
          onClose={() => setConfirmModalActive(false)}
          title={intl.formatMessage({id: 'confirm.remove'})}
          content={confirmContent()}
          confirmbutton={
            {
              name: intl.formatMessage({id: 'confirm.remove'}),
              class: 'btn btn-primary',
              onClick: () => { removeItem(removedItem); setConfirmModalActive(false) }
            }
          }
          />
        <ConfirmModal
          active={confirmArchModalActive}
          modalId='arhciveUserModal'
          onClose={() => setConfirmArchModalActive(false)}
          title={confirmModalTitle}
          content={confirmArchiveContent()}
          confirmbutton={
            {
              name: confirmModalTitle,
              class: 'btn btn-primary',
              onClick: () => { archiveUser(isArchive); setConfirmArchModalActive(false) }
            }
          }
          />
        <Spinner active={saving} />
        <ToastWrap active={toastActive} heading={toastHeader} text={toastText} type={toastType} setActive={(active: boolean) => setToastActive(active)} />
        <div className="action-buttons py-4 d-flex flex-row align-items-start">
            <div className="page-header me-4">
            {(user?.name?.length > 0) ?
            <h1><FormattedMessage id="users.title.user"/>{user.name}</h1> :
            <h1><FormattedMessage id="users.adduser"/></h1>
            }
            </div>

            <div className="ms-auto d-flex flex-row align-items-center">
              { userState.archived &&
                <p className="state me-4 m-0 d-flex flex-row align-items-center ">
                  <i className="fa fa-archive me-2"></i>
                  <FormattedMessage id="users.archived"/>
                </p>
              }
              <button type="button" className="btn btn-secondary" onClick={() => { navigate(-1); }}>
                <i className="fa fa-close me-2"></i>
                <FormattedMessage id="button.close"/>
              </button>
              {!userState.archived && userState.userId &&
                <button type="button" className="btn btn-secondary btn-archive"
                  onClick={() => confirmArchiveUser()}>
                  <i className="fa fa-archive me-2"></i>
                  <FormattedMessage id="button.archive"/>
                </button>
              }
              {userState.archived &&
                <button type="button" className="btn btn-secondary btn-activate"
                  onClick={(e) => confirmArchiveUser()}>
                  <i className="fa fa-check me-2"></i>
                  <FormattedMessage id="button.activate"/>
                </button>
              }
              <button type="button" className="btn btn-primary btn-save" disabled={!changed}
                onClick={(e) => saveUser(e)}>
                <i className="fa fa-save me-2"></i>
                <FormattedMessage id="button.save"/>
              </button>

            </div>

          </div>

          <div className="controls d-flex justify-content-end my-3">
            <button className="btn btn-link p-0"
              style={ !(expandDetails && expandCompanies) ? {} : {display: 'none'}}
              onClick={ () => { setExpandCompanies(true); setExpandDetails(true); }}>
              <FormattedMessage id="button.expandall" />
              <i aria-hidden="true" className="fa fa-chevron-down ms-2"></i>
            </button>
            <button className="btn btn-link p-0"
              style={ expandDetails && expandCompanies ? {} : {display: 'none'}}
              onClick={ () => { setExpandCompanies(false); setExpandDetails(false); }}>
              <FormattedMessage id="button.collapseall" />
              <i aria-hidden="true" className="fa fa-chevron-up ms-2"></i></button>
          </div>

          <details id="details" open={expandDetails}>
            <summary onClick={ (e) => { setExpandDetails(expandDetails => !expandDetails); e.preventDefault(); }}
              className={ changedDetails.details ? "changed" : "" }>
              <h2>
                  <FormattedMessage id="profile.userinfo"/>
              </h2>
            </summary>
            <div>

              <div className={"form-group " + validate('name')}>
                <label htmlFor="name" className="form-label"><FormattedMessage id="users.name"/></label>
                <input id="name" name="name" className={"form-control " + userStateClass.name}
                  type="text" value={userState.name} autoComplete="off"
                  onChange={update}/>
                <div aria-atomic="true" aria-live="polite">
                  <div id="fielderror1" className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="users.obligatory.info"/>
                  </div>
                </div>
              </div>

              <div className={"form-group " + validate('email')}>
                <label htmlFor="email" className="form-label"><FormattedMessage id="users.email"/></label>
                <input id="email" name="email" className={"form-control " + userStateClass.email}
                  type="text" value={userState.email} autoComplete="off"
                  onChange={update}/>
                <div aria-atomic="true" aria-live="polite">
                  <div id="fielderror2" className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    { userExistsError ? <FormattedMessage id="users.email.error"/> : <FormattedMessage id="users.obligatory.info"/>}
                  </div>
                </div>
               </div>

              <div className={"form-group " + validate('phone')}>
                <label htmlFor="phone" className="form-label"><FormattedMessage id="users.phone"/></label>
                <input id="phone" name="phone" className={"form-control " + userStateClass.phone}
                  type="text" value={userState.phone} autoComplete="off"
                  onChange={update}/>
                <div aria-atomic="true" aria-live="polite">
                  <div id="fielderror3" className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="users.obligatory.info"/>
                  </div>
                </div>
               </div>

               {/* <div className={"form-group " + validate('language')}>
                <label htmlFor="language" className="form-label"><FormattedMessage id="profile.defaultlanguage"/></label>
                <input id="language" name="language" className={"form-control " + userStateClass.language} type="text" value={userState.language}
                  onChange={update}/>
                <div aria-atomic="true" aria-live="polite">
                  <div id="fielderror4" className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="users.obligatory.info"/>
                  </div>
                </div>
               </div> */}

              <fieldset className="form-group checkbox-group">
 
                <div className="form-check checkboxgroup">
                  <label htmlFor="type1">
                    <input type="checkbox" className="form-check-input" id="type1" name="reporter" value="1"
                      checked={userState.reporter} onChange={update}/>
                    <span className="label-text"><FormattedMessage id="users.reporting"/></span>
                  </label>
                </div>
                <div className="form-check checkboxgroup">
                  <label htmlFor="type2">
                    <input type="checkbox" className="form-check-input" id="type2" name="information" value="2"
                      checked={userState.information} onChange={update}/>
                    <span className="label-text"><FormattedMessage id="users.info"/></span>
                  </label>
                </div>
               
              </fieldset>


            </div>
          </details>

          <details id="companies" open={expandCompanies}>
            <summary onClick={ (e) => { setExpandCompanies(expandCompanies => !expandCompanies); e.preventDefault(); }}
              className={ changedDetails.companies ? "changed" : "" }>
              <h2>
                  <FormattedMessage id="profile.usercompanies"/>
              </h2>
            </summary>
            <div>
              <div className="toolbar">
                <nav aria-label={ intl.formatMessage({ id: 'section.toolbar' })}>
                  <button className="btn btn-link p-0 btn-add"
                    disabled={userState?.userId ? false : true}
                    onClick={() => setModalActive(true)}>
                      <i className="fa fa fa-plus-circle me-2"></i>
                      <FormattedMessage id="users.addcompany"/>
                  </button>
                </nav>
              </div>
              {!userState?.userId &&
              <p className="info"><i className="fa fa-info-circle me-2"></i>
                <FormattedMessage id="users.addUser.info"/>
              </p>
              }
              {selectedCompanies.length > 0 &&
              <div className="list-group list-group-flush">
                {selectedCompanies.map((s: IUserCompanies, ind: number) =>
                  getCompanyItem(s, ind)
                )}
              </div>
              }
            </div>
          </details>

      </>
    );
};